import { MapPin, Storefront } from '@liveauctioneers/hammer-ui-core/icons';
import { StarRating } from '@liveauctioneers/hammer-ui-core/starRating';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useMemo } from 'react';

import { useAppSelector } from '@/redux/hooks';
import { getItemSummaryHouseReviewCountAndRating } from '@/redux/modules/item/summary/itemSummary.selectors';

const MAX_REVIEW_COUNT = 1000;

export type ItemCardSellerRowProps = {
    className?: string;
    itemId: number;
    location?: string;
    shouldHideRatingTrailingText?: boolean;
};

export const ItemCardSellerRow = ({
    className,
    itemId,
    location,
    shouldHideRatingTrailingText = false,
}: ItemCardSellerRowProps) => {
    const { houseRating, houseReviewCount, sellerName } = useAppSelector((state) =>
        getItemSummaryHouseReviewCountAndRating(state, itemId)
    );

    const houseRatingTrailingText = useMemo(() => {
        if (!houseReviewCount) {
            return undefined;
        }
        if (houseReviewCount > MAX_REVIEW_COUNT) {
            const countInThousands = Math.floor(houseReviewCount / 100) / 10;
            return `${countInThousands}k`;
        }
        return houseReviewCount.toString();
    }, [houseReviewCount]);

    return (
        <div
            className={cn(
                'mt-auto flex flex-col gap-y-2xs',
                // Keep review count in the DOM for SEO; hide it visually when space is constrained
                shouldHideRatingTrailingText && '[&_[data-hui-id=StarRating]>span:last-child]:hidden',
                className
            )}
        >
            {location && (
                <BodySecondary className="flex items-baseline gap-x-2xs text-xs text-text-secondary">
                    <span className="inline-flex shrink-0 translate-y-[2px] leading-none">
                        <MapPin size="xs" />
                    </span>
                    <span className="truncate">{location}</span>
                </BodySecondary>
            )}
            <div className="flex min-w-0 items-center gap-x-2xs">
                <BodySecondary className="flex min-w-0 flex-1 items-center gap-x-2xs text-xs text-text-secondary">
                    <span className="inline-flex shrink-0 leading-none">
                        <Storefront size="xs" />
                    </span>
                    <span className="min-w-0 truncate leading-none">{sellerName}</span>
                </BodySecondary>
                {Boolean(houseReviewCount) && (
                    <StarRating
                        averageRating={houseRating ?? 0}
                        className="shrink-0"
                        compact
                        size="xs"
                        trailingText={houseRatingTrailingText}
                    />
                )}
            </div>
        </div>
    );
};
